import { PageShell } from './PageShell.js';
import type { WashedLocale } from '@washed/i18n';
import styles from './PageShell.module.css';

interface PageProps {
  locale: WashedLocale;
  onLocaleChange: (locale: WashedLocale) => void;
}

interface QA {
  q: string;
  /** Plain-text answer (used for both the visible copy and the JSON-LD). */
  a: string;
}

const FAQ: Record<WashedLocale, { eyebrow: string; title: string; intro: string; items: QA[] }> = {
  fr: {
    eyebrow: 'Washed · FAQ',
    title: 'Questions fréquentes',
    intro:
      'Tout ce qu’il faut savoir sur l’abonnement Washed : une laveuse de confiance, chez vous, à Lomé.',
    items: [
      {
        q: 'Qu’est-ce que Washed ?',
        a: 'Washed est un abonnement mensuel à Lomé : une laveuse formée passe chez vous une ou deux fois par mois et lave votre linge sur place, avec votre eau et votre savon. Washed n’est pas une laverie — c’est la plateforme qui organise la mise en relation, le planning et le paiement.',
      },
      {
        q: 'Washed est-il une laverie ou un pressing ?',
        a: 'Non. Washed n’est pas une laverie ni un pressing : votre linge n’est jamais emporté. Une laveuse vient le laver chez vous, sur place.',
      },
      {
        q: 'Le linge est-il lavé chez moi ou emporté ?',
        a: 'Tout est lavé chez vous, sur place, avec votre eau et votre savon. Rien ne quitte votre domicile — vous gardez le contrôle sur vos vêtements et vos produits.',
      },
      {
        q: 'Combien coûte l’abonnement ?',
        a: 'Six formules : Solo 1× à 4 500 F CFA/mois et Solo 2× à 8 500 F CFA/mois pour 1 personne ; Duo 1× à 6 000 F CFA/mois et Duo 2× à 11 500 F CFA/mois pour 2 personnes ; Foyer 1× à 9 500 F CFA/mois et Foyer 2× à 18 000 F CFA/mois pour 3 à 5 personnes.',
      },
      {
        q: 'Comment payer ?',
        a: 'Par mobile money uniquement : Mixx by Yas ou Flooz. Pas de carte bancaire. Vous payez Washed, pas la laveuse directement. Rien n’est prélevé à l’inscription : le premier prélèvement a lieu quand le bureau confirme votre laveuse, avant votre première visite, puis tous les 30 jours.',
      },
      {
        q: 'Combien de linge puis-je faire laver par visite ?',
        a: 'La quantité prise en charge dépend des conditions de votre formule et du cadre de service présenté au moment de l’inscription.',
      },
      {
        q: 'Puis-je annuler ou suspendre ?',
        a: 'Oui, selon les conditions de votre formule. Les modalités de suspension et d’annulation sont présentées avant la souscription.',
      },
      {
        q: 'Est-ce toujours la même laveuse ?',
        a: 'Votre laveuse habituelle est réassignée à vos visites par défaut, afin de construire une relation de confiance dans la durée. Si elle est indisponible ou si un changement est nécessaire, Washed peut organiser un remplacement.',
      },
      {
        q: 'Qui sont les laveuses Washed ?',
        a: 'Des laveuses formées et vérifiées à Lomé. Washed cherche à vous attribuer durablement la même professionnelle, tout en gardant la possibilité d’organiser un remplacement en cas d’indisponibilité.',
      },
      {
        q: 'Que dois-je fournir ?',
        a: 'Votre eau et votre savon, comme d’habitude. La laveuse apporte son savoir-faire et son soin ; vous n’avez rien d’autre à préparer.',
      },
      {
        q: 'Où le service est-il disponible ?',
        a: 'À Lomé, au Togo. Washed sert tous les quartiers de la ville.',
      },
      {
        q: 'Comment commencer ?',
        a: 'Inscrivez-vous avec votre numéro et votre quartier. L’équipe Washed vous recontacte pour la suite.',
      },
      {
        q: 'Comment devenir laveuse Washed ?',
        a: 'Inscrivez-vous sur la page « Devenir laveuse ». Nous recrutons des laveuses à Lomé pour des missions régulières auprès de foyers proches de chez elles.',
      },
    ],
  },
  en: {
    eyebrow: 'Washed · FAQ',
    title: 'Frequently asked questions',
    intro:
      'Everything you need to know about a Washed subscription: a trusted washerwoman, at home, in Lomé.',
    items: [
      {
        q: 'What is Washed?',
        a: 'Washed is a monthly in-home laundry subscription in Lomé. A trained washerwoman comes to your home once or twice a month and washes your laundry on-site, using your own water and soap. Washed coordinates the match, schedule and payment.',
      },
      {
        q: 'Is Washed a laundromat or a dry cleaner?',
        a: 'No. Washed is not a laundromat or dry cleaner: your laundry is never taken away. A washerwoman washes it at your home, on-site.',
      },
      {
        q: 'Is laundry washed at my home or taken away?',
        a: 'Everything is washed at your home, on-site, using your own water and soap. Nothing leaves your home — you stay in control of your clothes and products.',
      },
      {
        q: 'How much does it cost?',
        a: 'Six plans: Solo 1× at CFA 4,500/month and Solo 2× at CFA 8,500/month for 1 person; Duo 1× at CFA 6,000/month and Duo 2× at CFA 11,500/month for 2 people; Household 1× at CFA 9,500/month and Household 2× at CFA 18,000/month for 3 to 5 people.',
      },
      {
        q: 'How do I pay?',
        a: 'Mobile money only: Mixx by Yas or Flooz. No bank cards. You pay Washed, not the washerwoman directly. Nothing is charged at sign-up: the first charge happens when the office confirms your washerwoman, before your first visit, then every 30 days.',
      },
      {
        q: 'How much laundry can I have washed per visit?',
        a: 'The amount covered depends on your plan conditions and the service rules shown when you sign up.',
      },
      {
        q: 'Can I cancel or pause?',
        a: 'Yes, according to your plan conditions. The pause and cancellation terms are shown before subscription.',
      },
      {
        q: 'Is it always the same washerwoman?',
        a: 'Your regular washerwoman is assigned to your visits by default so a trusted relationship can build over time. If she is unavailable or a change is needed, Washed can arrange a replacement.',
      },
      {
        q: 'Who are the Washed washerwomen?',
        a: 'Trained and vetted washerwomen in Lomé. Washed aims to keep the same professional assigned to your household over time while retaining the ability to arrange a replacement when needed.',
      },
      {
        q: 'What do I need to provide?',
        a: 'Your water and soap, as usual. The washerwoman brings her skill and care; there is nothing else to prepare.',
      },
      {
        q: 'Where is the service available?',
        a: 'In Lomé, Togo. Washed serves every neighbourhood in the city.',
      },
      {
        q: 'How do I get started?',
        a: 'Sign up with your number and neighbourhood. The Washed team will get back to you about next steps.',
      },
      {
        q: 'How do I become a Washed washerwoman?',
        a: 'Sign up on the “Become a washerwoman” page. We are recruiting washerwomen in Lomé for regular assignments with households close to where they live.',
      },
    ],
  },
};

export function FaqPage({ locale, onLocaleChange }: PageProps) {
  const c = FAQ[locale];

  const structured = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': 'https://washedafrica.com/faq#faqpage',
    inLanguage: locale,
    isPartOf: { '@id': 'https://washedafrica.com/#website' },
    about: { '@id': 'https://washedafrica.com/#organization' },
    mainEntity: c.items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <PageShell
      locale={locale}
      onLocaleChange={onLocaleChange}
      eyebrow={c.eyebrow}
      title={c.title}
      intro={c.intro}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }}
      />
      <dl className={styles.faq}>
        {c.items.map((item) => (
          <div key={item.q} className={styles.faqItem}>
            <dt className={styles.faqQ}>{item.q}</dt>
            <dd className={styles.faqA}>{item.a}</dd>
          </div>
        ))}
      </dl>
    </PageShell>
  );
}
